import type { Movie } from '../mocks/movies';

export interface FilterOptions {
  region?: string;
  year?: number | null;
  genre?: string;
  minRating?: number;
  keyword?: string;
}

export function filterMovies(list: Movie[], options: FilterOptions): Movie[] {
  return list.filter((movie) => {
    if (options.region && options.region !== '全部' && movie.region !== options.region) return false;
    if (options.year && movie.year !== options.year) return false;
    if (options.genre && options.genre !== '全部' && !movie.genres.includes(options.genre)) return false;
    if (options.minRating && movie.rating < options.minRating) return false;
    if (options.keyword) {
      const kw = options.keyword.trim().toLowerCase();
      if (kw) {
        const haystack = [movie.title, movie.region, movie.director, ...movie.genres, ...movie.actors, ...movie.tags]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(kw)) return false;
      }
    }
    return true;
  });
}

export type SortKey = 'updated' | 'views' | 'year' | 'rating';

export function sortMovies(list: Movie[], key: SortKey): Movie[] {
  const sorted = [...list];
  switch (key) {
    case 'views':
      return sorted.sort((a, b) => b.views - a.views);
    case 'year':
      return sorted.sort((a, b) => b.year - a.year);
    case 'rating':
      return sorted.sort((a, b) => b.rating - a.rating);
    case 'updated':
    default:
      return sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }
}

export function getMovieById(list: Movie[], id: string): Movie | undefined {
  return list.find((movie) => movie.id === id);
}

export function getRelatedMovies(list: Movie[], movie: Movie, limit = 6): Movie[] {
  return list
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const tagOverlap = item.tags.filter((tag) => movie.tags.includes(tag)).length;
      const genreOverlap = item.genres.filter((genre) => movie.genres.includes(genre)).length;
      const typeBonus = item.type === movie.type ? 1 : 0;
      return { item, score: tagOverlap * 2 + genreOverlap * 3 + typeBonus };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return `${views}`;
}

export function resolveCategoryMeta(type: string): { title: string; subtitle: string; baseList: (list: Movie[]) => Movie[] } {
  const decoded = decodeURIComponent(type);
  if (decoded === 'movie') {
    return {
      title: '高清电影',
      subtitle: '精选国内外院线大片与经典佳作，全部配备中文字幕',
      baseList: (list) => list.filter((m) => m.type === 'movie'),
    };
  }
  if (decoded === 'tv') {
    return {
      title: '电视剧',
      subtitle: '热播剧集与口碑长剧一次看爽，更新同步中文字幕',
      baseList: (list) => list.filter((m) => m.type === 'tv'),
    };
  }
  if (decoded === 'featured') {
    return {
      title: '编辑专题',
      subtitle: '由编辑团队精心策划的观影专题，按主题发现好片',
      baseList: (list) => list.filter((m) => m.featured),
    };
  }
  return {
    title: `${decoded}片`,
    subtitle: `「${decoded}」类型精选影视作品，高清中文字幕同步呈现`,
    baseList: (list) => list.filter((m) => m.genres.includes(decoded)),
  };
}